import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { axiosInstance } from '../lib/api-client';
import { queryKeys } from '../lib/react-query';
import { toast } from 'sonner';

// ============ Admin Commission Hooks ============

export function useCommissionTransactions() {
  return useQuery({
    queryKey: queryKeys.admin.commissions.transactions,
    queryFn: async () => {
      const response = await axiosInstance.get('/admin/commissions/transactions');
      return response.data.success ? response.data.data : [];
    },
    staleTime: 1000 * 60 * 2, // 2 minutes - refresh more often for financial data
  });
}

export function useCommissionMarketingUsers() {
  return useQuery({
    queryKey: queryKeys.admin.commissions.marketingUsers,
    queryFn: async () => {
      const response = await axiosInstance.get('/admin/commissions/marketing-users');
      return response.data.success ? response.data.data : [];
    },
    staleTime: 1000 * 60 * 3, // 3 minutes
  });
}

export function useCommissionSettings() {
  return useQuery({
    queryKey: queryKeys.admin.commissions.settings,
    queryFn: async () => {
      const response = await axiosInstance.get('/admin/commissions/settings');
      return response.data.success ? response.data.data : { commissionRate: 50 };
    },
    staleTime: 1000 * 60 * 10, // 10 minutes - settings don't change often
  });
}

export function useApproveCommission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (transactionId: string) => {
      const response = await axiosInstance.post(`/admin/commissions/transactions/${transactionId}/approve`);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate and refetch relevant queries
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.commissions.transactions });
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.commissions.marketingUsers });
      toast.success('Commission approved successfully');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Failed to approve commission';
      toast.error(errorMessage);
    },
  });
}

export function useRejectCommission() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ transactionId, reason }: { transactionId: string; reason: string }) => {
      const response = await axiosInstance.post(`/admin/commissions/transactions/${transactionId}/reject`, { reason });
      return response.data;
    },
    onSuccess: () => {
      // Invalidate and refetch relevant queries
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.commissions.transactions });
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.commissions.marketingUsers });
      toast.success('Commission rejected');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Failed to reject commission';
      toast.error(errorMessage);
    },
  });
}

export function useUpdateCommissionRate() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (commissionRate: number) => {
      const response = await axiosInstance.put('/admin/commissions/settings', { commissionRate });
      return response.data;
    },
    onSuccess: () => {
      // Invalidate settings query to refetch new rate
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.commissions.settings });
      toast.success('Commission rate updated successfully');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Failed to update commission rate';
      toast.error(errorMessage);
    },
  });
}

export function useUpdateEmploymentType() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ userId, employmentType }: { userId: string; employmentType: 'commission' | 'salary' }) => {
      const response = await axiosInstance.put(`/admin/commissions/marketing-users/${userId}/employment-type`, { employmentType });
      return response.data;
    },
    onSuccess: () => {
      // Invalidate marketing users query to refetch updated data
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.commissions.marketingUsers });
      toast.success('Employment type updated successfully');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Failed to update employment type';
      toast.error(errorMessage);
    },
  });
}

// ============ Admin Coupon Hooks ============

export function useCoupons(filters?: any) {
  return useQuery({
    queryKey: queryKeys.coupons.list(filters),
    queryFn: async () => {
      const response = await axiosInstance.get('/coupons', { params: filters });
      if (!response.data.success) return [];

      // Handle both { data: { coupons: [...] } } and { data: [...] }
      if (response.data.data && response.data.data.coupons) {
        return response.data.data.coupons;
      }
      return Array.isArray(response.data.data) ? response.data.data : [];
    },
    staleTime: 1000 * 60 * 3, // 3 minutes
  });
}

export function usePlans() {
  return useQuery({
    queryKey: queryKeys.plans.list,
    queryFn: async () => {
      const response = await axiosInstance.get('/plans');
      if (!response.data.success) return [];

      // Handle both { data: { plans: [...] } } and { data: [...] }
      if (response.data.data && response.data.data.plans) {
        return response.data.data.plans;
      }
      return Array.isArray(response.data.data) ? response.data.data : [];
    },
    staleTime: 1000 * 60 * 10, // 10 minutes - plans don't change often
  });
}

export function usePremiumPlans() {
  return useQuery({
    queryKey: ['premiumPlans'],
    queryFn: async () => {
      // Use admin endpoint to get all premium plans
      const response = await axiosInstance.get('/admin/premium-plans');
      if (!response.data.success) return [];

      const data = response.data.data;
      if (!data) return [];

      // Handle { data: { premiumPlans: [...] } }
      if (data.premiumPlans && Array.isArray(data.premiumPlans)) {
        return data.premiumPlans;
      }

      // Handle { data: [...] } (old format)
      if (Array.isArray(data)) {
        return data;
      }

      return [];
    },
    staleTime: 1000 * 60 * 10, // 10 minutes - plans don't change often
  });
}

export function useCreateCoupon() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (couponData: any) => {
      const response = await axiosInstance.post('/coupons', couponData);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate coupons query to refetch updated list
      queryClient.invalidateQueries({ queryKey: queryKeys.coupons.all });
      toast.success('Coupon created successfully');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Failed to create coupon';
      toast.error(errorMessage);
    },
  });
}

export function useDeleteCoupon() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (couponId: string) => {
      const response = await axiosInstance.delete(`/coupons/${couponId}`);
      return response.data;
    },
    onSuccess: () => {
      // Invalidate coupons query to refetch updated list
      queryClient.invalidateQueries({ queryKey: queryKeys.coupons.all });
      toast.success('Coupon deleted successfully');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Failed to delete coupon';
      toast.error(errorMessage);
    },
  });
}

export function useToggleCouponStatus() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ couponId, isActive }: { couponId: string; isActive: boolean }) => {
      const response = await axiosInstance.patch(`/coupons/${couponId}/toggle-status`, { isActive });
      return response.data;
    },
    onSuccess: () => {
      // Invalidate coupons query to refetch updated list
      queryClient.invalidateQueries({ queryKey: queryKeys.coupons.all });
      toast.success('Coupon status updated');
    },
    onError: (error: any) => {
      const errorMessage = error?.response?.data?.message || 'Failed to update coupon status';
      toast.error(errorMessage);
    },
  });
}

// ============ Admin Dashboard Stats ============

export function useDashboardStats() {
  return useQuery({
    queryKey: queryKeys.admin.dashboardStats,
    queryFn: async () => {
      const response = await axiosInstance.get('/admin/dashboard-stats');
      return response.data.success ? response.data.stats : null;
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

// ============ Admin Users ============

export function useAdminUsers(filters?: any) {
  return useQuery({
    queryKey: queryKeys.admin.users(filters),
    queryFn: async () => {
      const response = await axiosInstance.get('/admin/users', { params: filters });
      return response.data;
    },
    staleTime: 1000 * 60 * 3, // 3 minutes
    enabled: !!localStorage.getItem('token'), // Only fetch if authenticated
  });
}

// ============ Admin Reported Users ============

export function useReportedUsers(filters?: any) {
  return useQuery({
    queryKey: queryKeys.admin.reportedUsers(filters),
    queryFn: async () => {
      const response = await axiosInstance.get('/admin/reported-users', { params: filters });
      return response.data;
    },
    staleTime: 1000 * 60 * 3, // 3 minutes
  });
}

// ============ Profile Edit Approval ============

export function useProfileEditRequests(filters?: any) {
  return useQuery({
    queryKey: queryKeys.admin.profileEditRequests(filters),
    queryFn: async () => {
      const response = await axiosInstance.get('/admin/profile-edit-requests', { params: filters });
      return response.data.success ? response.data.data : { requests: [], total: 0 };
    },
    staleTime: 1000 * 60 * 2, // 2 minutes - check frequently for new requests
  });
}

export function useApproveProfileEdit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (requestId: string) => {
      const response = await axiosInstance.post(`/admin/profile-edit-requests/${requestId}/approve`);
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.profileEditRequests() });
      toast.success('Profile changes approved');
    },
  });
}

export function useRejectProfileEdit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ requestId, reason }: { requestId: string; reason: string }) => {
      const response = await axiosInstance.post(`/admin/profile-edit-requests/${requestId}/reject`, { reason });
      return response.data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.admin.profileEditRequests() });
      toast.success('Profile changes rejected');
    },
  });
}